import {
    IApiDigitalAdviceQuestion,
    IApiDisclaimerResults,
    IApiFinalRecommendedCoverResults,
    IApiInterimRecommendedCoverResults,
    IApiRecommendedCover
} from "../../interfaces/IApiDigitalAdviceQuestion";
import {
    IDigitalQuestion,
    IDigitalQuestionOption,
    IDisclaimerResults,
    IFinalRecommendedCoverResults,
    IInterimRecommendedCoverResults,
    IQuestionOptionImage,
    IRecommendedCover
} from "../../interfaces/IDigitalAdviceData";

export const parseQuestionPictures = (
    images: Array<IQuestionOptionImage>
): IQuestionOptionImage[] => {
    return images?.map((img) => ({
        dapQuestionOptionImageId: img.dapQuestionOptionImageId,
        imgUrl: img.imgUrl,
        orderBy: img.orderBy,
        zIndex: img.zIndex,
        responseValueMax: img.responseValueMax,
        responseValueMin: img.responseValueMin
    }));
};

export const parseQuestionPlaceholderPictures = (
    images: Array<IQuestionOptionImage>
): IQuestionOptionImage[] => {
    return images?.map((img) => ({
        dapQuestionPlaceholderImageId: img.dapQuestionPlaceholderImageId,
        imgUrl: img.imgUrl,
        orderBy: img.orderBy,
        zIndex: img.zIndex
    }));
};

export const parseRecommendAdultCover = (
    adultCover: IApiRecommendedCover
): IRecommendedCover => {
    return {
        adviceAmount: adultCover.adviceAmount,
        lifeCover: adultCover.lifeCoverAmount,
        formattedLifeCover: adultCover.formattedLifeCover,
        tpdCover: adultCover.tpdCover,
        formattedTPDCover: adultCover.formattedTPDCover,
        traumaCoverAmount: adultCover.traumaCoverAmount,
        formattedTraumaCover: adultCover.formattedTraumaCover,
        ipCoverAmountMonthly: adultCover.ipCoverAmountMonthly,
        formattedIPCoverMonthly: adultCover.formattedIPCoverMonthly,
        healthCoverExcess: adultCover.healthCoverExcess,
        healthCoverExcessFormatted: adultCover.healthCoverExcessFormatted,
        healthCoverExcessFinalFormatted: adultCover.healthCoverExcessFinalFormatted,
        healthExcessAmount: adultCover.healthExcessAmount,
        ipCoverWaitPeriod: adultCover.ipCoverWaitPeriod,
        trimmedIPCoverWaitPeriod: adultCover.trimmedIPCoverWaitPeriod,
        hasExceededCover: adultCover?.hasExceededCover || false
    };
};

export const parseQuestion = (question: IApiDigitalAdviceQuestion): IDigitalQuestion => {
    const options = question.options?.map(
        (opt): IDigitalQuestionOption => ({
            id: opt.id,
            questionId: opt.questionId,
            label: opt.label,
            responseValue: opt.responseValue,
            orderBy: opt.orderBy,
            imgUrl: opt.imgUrl,
            questionOptionImage: parseQuestionPictures(opt.questionOptionImage)
        })
    );

    const subQuestions = question.subQuestions?.map(
        (opt): IDigitalQuestion => ({
            id: opt.id,
            label: opt.label,
            tooltip: opt.tooltip,
            placeholder: opt.placeholder,
            orderBy: opt.orderBy,
            parentQuestionId: opt.parentQuestionId,
            sectionId: opt.sectionId,
            controlTypeId: opt.controlTypeId,
            imgUrl: opt.imgUrl,
            options: opt.options,
            deselectOthers: opt.deselectOthers,
            questionOptionImage: parseQuestionPictures(opt.questionOptionImage)
        })
    );

    return {
        id: question.id,
        label: question.label,
        subHeading: question.subHeading,
        tooltip: question.tooltip,
        placeholder: question.placeholder,
        orderBy: question.orderBy,
        parentQuestionId: question.parentQuestionId,
        sectionId: question.sectionId,
        controlTypeId: question.controlTypeId,
        imgUrl: question.imgUrl,
        options,
        hasSubQuestion: question.hasSubQuestion,
        subQuestions,
        showFrameLabel: question.showFrameLabel,
        frameLabel: question.frameLabel,
        nextQuestionOrder: question.nextQuestionOrder,
        previousQuestionId: question.previousQuestionId,
        generateQuoteGroupId: question.generateQuoteGroupId,
        baseImages: parseQuestionPictures(question.baseImages),
        placeholderImages: parseQuestionPlaceholderPictures(question.placeholderImages),
        questionOptionImage: parseQuestionPictures(question.questionOptionImage),
        nextQuestionPlaceholderImages: parseQuestionPlaceholderPictures(
            question.nextQuestionPlaceholderImages
        )
    };
};

export const parseInterimRecommendedCover = (
    cover: IApiInterimRecommendedCoverResults
): IInterimRecommendedCoverResults => {
    return {
        includeAdult2: cover.includeAdult2,
        client: parseRecommendAdultCover(cover.adult1),
        partner: parseRecommendAdultCover(cover.adult2)
    };
};

export const parseFinalRecommendedCover = (
    cover: IApiFinalRecommendedCoverResults
): IFinalRecommendedCoverResults => {
    return {
        requestCallback: cover?.requestCallback || false,
        hasExistingInsuranceType: cover?.hasExistingInsuranceType || false,
        hasHealthInsurance: cover?.hasHealthInsurance || false,
        result: {
            budget: {
                includeAdult2: cover.includeAdult2,
                client: {
                    ...parseRecommendAdultCover(cover.result?.budget.adult1),
                    covers: cover.result?.budget.adult1.covers
                },
                partner: {
                    ...parseRecommendAdultCover(cover.result?.budget.adult2),
                    covers: cover.result?.budget.adult2.covers
                }
            },
            goals: {
                includeAdult2: cover.includeAdult2,
                client: {
                    ...parseRecommendAdultCover(cover.result?.goals.adult1),
                    covers: cover.result?.goals.adult1.covers
                },
                partner: {
                    ...parseRecommendAdultCover(cover.result?.goals.adult2),
                    covers: cover.result?.goals.adult2.covers
                }
            },
            ultimate: {
                includeAdult2: cover.includeAdult2,
                client: {
                    ...parseRecommendAdultCover(cover.result?.ultimate.adult1),
                    covers: cover.result?.ultimate.adult1.covers
                },
                partner: {
                    ...parseRecommendAdultCover(cover.result?.ultimate.adult2),
                    covers: cover.result?.ultimate.adult2.covers
                }
            }
        }
    };
};

export const parseDisclaimerResults = (
    res: IApiDisclaimerResults
): IDisclaimerResults => {
    return {
        sessionUserId: res?.sessionId,
        understoodSuitability: res.understoodSuitability,
        understoodReplacement: res.understoodReplacement,
        recommendationEmail: res.recommendationEmail,
        isDone: res.isDone
    };
};
